import { Loader, Table, useMantineTheme } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { format } from "date-fns";
import React, { useState } from "react";
import { match } from "ts-pattern";
import { Analysis, ProcessingStatus } from "@/models/analysis";
import { AnalysisService } from "@/services/analysis/analysis.service";
import { downloadCsv } from "@/utils/csv";
import { showError } from "@/utils/notification";

interface Props {
  analyses: Analysis[];
  isLoading: boolean;
  getCategoryTreeVersion: (categoryTreeId: string) => string | undefined;
}

const translateStatus = (status: ProcessingStatus) =>
  match(status)
    .with("QUEUED", () => "待機中")
    .with("DATA_LOAD_RUNNING", () => "データインポート実行中")
    .with("CATEGORY_CLASSIFICATION_RUNNING", () => "カテゴリ分類・生成実行中")
    .with("SHORT_SUMMARIZATION_RUNNING", () => "ショートサマリー作成中")
    .with("CATEGORY_SUMMARIZATION_RUNNING", () => "要約生成中")
    .with("RAG_INDEXING_RUNNING", () => "チャット分析用データ作成中")
    .with("VIEW_DATA_REFRESH_RUNNING", () => "分析画面更新中")
    .with("PROBLEM_CLASSIFICATION_RUNNING", () => "分析実行中")
    .with(
      "DATA_LOAD_FAILED",
      "CATEGORY_CLASSIFICATION_FAILED",
      "SHORT_SUMMARIZATION_FAILED",
      "CATEGORY_SUMMARIZATION_FAILED",
      "PROBLEM_CLASSIFICATION_FAILED",
      "VIEW_DATA_REFRESH_FAILED",
      "RAG_INDEXING_FAILED",
      () => "失敗",
    )
    .with("COMPLETED", () => "完了")
    .exhaustive();

export const AnalysisTable: React.FC<Props> = ({ analyses, isLoading, getCategoryTreeVersion }) => {
  const theme = useMantineTheme();
  const [downloadingAnalysisId, setDownloadingAnalysisId] = useState<string | null>(null);

  const handleDownload = async (analysisId: string) => {
    if (downloadingAnalysisId) return;

    try {
      setDownloadingAnalysisId(analysisId);
      const data = await AnalysisService.downloadCsv(analysisId);
      const analysis = analyses?.find((a) => a.id === analysisId);
      if (analysis) {
        const fileName = `${analysis.year}${analysis.month}_${format(analysis.startedAt, "yyyyMMddHHmmss")}.csv`;
        downloadCsv(data, fileName);
      }
      setDownloadingAnalysisId(null);
    } catch (error) {
      showError("CSVダウンロードに失敗しました。");
      console.error(error);
    }
  };

  if (isLoading) return <div>loading...</div>;

  return (
    <Table data-testid="analysis-table">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>#</Table.Th>
          <Table.Th>分析タイトル</Table.Th>
          <Table.Th>対象期間</Table.Th>
          <Table.Th>定期分析区分</Table.Th>
          <Table.Th>カテゴリバージョン</Table.Th>
          <Table.Th>ステータス</Table.Th>
          <Table.Th>開始日時</Table.Th>
          <Table.Th style={{ textAlign: "center" }}>分析結果</Table.Th>
          {
            /* <Table.Th>終了日時</Table.Th> */
          }
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {analyses?.length > 0
          ? analyses.map((analysis, index) => (
            <Table.Tr key={analysis.id}>
              <Table.Td>{index + 1}</Table.Td>
              <Table.Td maw="40vw">{analysis.title}</Table.Td>
              <Table.Td>
                {format(analysis.startDate, "yyyy-MM-dd") + " 〜 " + format(analysis.endDate, "yyyy-MM-dd")}
              </Table.Td>
              <Table.Td>{analysis.period ?? "-"}</Table.Td>
              <Table.Td>{getCategoryTreeVersion(analysis.categoryTreeId) ?? ""}</Table.Td>
              <Table.Td>{translateStatus(analysis.status)}</Table.Td>
              <Table.Td>{format(analysis.startedAt, "yyyy-MM-dd HH:mm:ss")}</Table.Td>
              <Table.Td align="center" width={90}>
                {downloadingAnalysisId === analysis.id
                  ? <Loader color="blue" size={24} />
                  : (
                    <IconDownload
                      color={analysis.status === "COMPLETED" && downloadingAnalysisId === null
                        ? theme.colors.blue[4]
                        : theme.colors.gray[4]}
                      style={{
                        cursor: analysis.status === "COMPLETED" && downloadingAnalysisId === null
                          ? "pointer"
                          : undefined,
                      }}
                      onClick={() => handleDownload(analysis.id)}
                    />
                  )}
              </Table.Td>
              {
                /* <Table.Td>{format(analysis.stoppedAt, "yyyy-MM-dd H:m:s")}</Table.Td> */
              }
            </Table.Tr>
          ))
          : (
            <Table.Tr>
              <Table.Td colSpan={5}>実行済の分析はありません</Table.Td>
            </Table.Tr>
          )}
      </Table.Tbody>
    </Table>
  );
};
